import { useState } from "react";
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  GripVertical,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "textarea", label: "Text Area" },
  { value: "select", label: "Dropdown" },
  { value: "country", label: "Country" },
  { value: "checkbox", label: "Checkbox" },
];

const DEFAULT_FIELDS = [
  { name: "firstName", label: "First Name", type: "text", required: true, placeholder: "" },
  { name: "lastName", label: "Last Name", type: "text", required: true, placeholder: "" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "" },
  { name: "company", label: "Company", type: "text", required: false, placeholder: "" },
  { name: "phone", label: "Phone", type: "phone", required: false, placeholder: "" },
  { name: "jobTitle", label: "Job Title", type: "text", required: false, placeholder: "" },
  { name: "country", label: "Country", type: "country", required: true, placeholder: "" },
];

const NEWSLETTER_FIELDS = [
  { name: "firstName", label: "First Name", type: "text", required: true, placeholder: "" },
  { name: "lastName", label: "Last Name", type: "text", required: true, placeholder: "" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "" },
  { name: "company", label: "Company", type: "text", required: false, placeholder: "" },
  { name: "country", label: "Country", type: "country", required: true, placeholder: "" },
];

const SALES_FIELDS = [
  { name: "firstName", label: "First Name", type: "text", required: true, placeholder: "" },
  { name: "lastName", label: "Last Name", type: "text", required: true, placeholder: "" },
  { name: "email", label: "Business Email", type: "email", required: true, placeholder: "" },
  { name: "company", label: "Company", type: "text", required: true, placeholder: "" },
  { name: "jobTitle", label: "Job Title", type: "text", required: true, placeholder: "" },
  { name: "phone", label: "Phone", type: "phone", required: false, placeholder: "" },
  { name: "country", label: "Country", type: "country", required: true, placeholder: "" },
  { name: "message", label: "How can we help?", type: "textarea", required: false, placeholder: "Tell us about your needs..." },
];

export const LANDING_TEMPLATES = [
  {
    id: "newsletter",
    label: "Newsletter Sign-Up",
    description: "Collect subscribers for the FGF newsletter",
    fields: NEWSLETTER_FIELDS,
    submitLabel: "Subscribe",
    thankYouMessage: "Thank you for subscribing! You'll receive our next newsletter shortly.",
    sourceTags: ["newsletter", "lead"],
    sourceLabel: "newsletter-signup",
  },
  {
    id: "sales",
    label: "Sales Inquiry",
    description: "Capture leads and pass details to the sales team",
    fields: SALES_FIELDS,
    submitLabel: "Get in Touch",
    thankYouMessage: "Thank you for your interest. A member of our team will be in touch shortly.",
    sourceTags: ["lead", "sales-inquiry"],
    sourceLabel: "landing-page",
  },
  {
    id: "custom",
    label: "Custom Form",
    description: "Start with default fields and customise",
    fields: DEFAULT_FIELDS,
    submitLabel: "Submit",
    thankYouMessage: "Thank you for your submission. We will be in touch shortly.",
    sourceTags: ["lead"],
    sourceLabel: "landing-page",
  },
];

export function getDefaultFormConfig(templateId) {
  const tpl = LANDING_TEMPLATES.find((t) => t.id === templateId) || LANDING_TEMPLATES[2];
  return {
    fields: [...tpl.fields],
    submitLabel: tpl.submitLabel,
    thankYouMessage: tpl.thankYouMessage,
    thankYouRedirect: "",
    notifyEmails: [],
    sourceTags: [...tpl.sourceTags],
    sourceLabel: tpl.sourceLabel,
  };
}

export default function FormBuilder({ formConfig, onChange }) {
  const [addingField, setAddingField] = useState(false);
  const [newField, setNewField] = useState({ name: "", label: "", type: "text", required: false, placeholder: "", options: "" });

  const fields = formConfig.fields || [];

  const updateFields = (updated) => {
    onChange({ ...formConfig, fields: updated });
  };

  const moveField = (idx, dir) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    updateFields(next);
  };

  const removeField = (idx) => {
    updateFields(fields.filter((_, i) => i !== idx));
  };

  const toggleRequired = (idx) => {
    const next = [...fields];
    next[idx] = { ...next[idx], required: !next[idx].required };
    updateFields(next);
  };

  const addField = () => {
    if (!newField.name || !newField.label) return;
    const field = {
      name: newField.name.replace(/[^a-zA-Z0-9_]/g, ""),
      label: newField.label,
      type: newField.type,
      required: newField.required,
      placeholder: newField.placeholder,
    };
    if (newField.type === "select" && newField.options) {
      field.options = newField.options.split(",").map((o) => o.trim()).filter(Boolean);
    }
    updateFields([...fields, field]);
    setNewField({ name: "", label: "", type: "text", required: false, placeholder: "", options: "" });
    setAddingField(false);
  };

  const updateSetting = (key, value) => {
    onChange({ ...formConfig, [key]: value });
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: 13,
    fontFamily: fonts.body,
    background: colors.bgCard,
    color: colors.text,
  };

  const smallBtn = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    background: "transparent",
    transition: "background 0.15s",
  };

  return (
    <div>
      {/* Field list */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <h4
          style={{
            fontFamily: fonts.heading,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            margin: 0,
          }}
        >
          Form Fields
        </h4>
        <span style={{ fontSize: 11, color: colors.textSecondary }}>
          {fields.length} field{fields.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
        {fields.map((field, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
            }}
          >
            <GripVertical size={14} color={colors.textSecondary} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{field.label}</span>
                <span
                  style={{
                    fontSize: 10,
                    padding: "1px 6px",
                    borderRadius: 3,
                    background: "rgba(99,102,241,0.08)",
                    color: "#6366f1",
                    fontWeight: 600,
                    fontFamily: fonts.mono,
                  }}
                >
                  {field.type}
                </span>
                {field.required && (
                  <span
                    style={{
                      fontSize: 10,
                      padding: "1px 6px",
                      borderRadius: 3,
                      background: "rgba(239,68,68,0.08)",
                      color: colors.danger,
                      fontWeight: 600,
                    }}
                  >
                    required
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: colors.textSecondary, fontFamily: fonts.mono }}>
                {field.name}
              </div>
            </div>
            <button
              onClick={() => toggleRequired(idx)}
              title={field.required ? "Make optional" : "Make required"}
              style={{
                ...smallBtn,
                color: field.required ? colors.danger : colors.textSecondary,
                fontSize: 11,
                fontWeight: 700,
                width: "auto",
                padding: "4px 8px",
              }}
            >
              {field.required ? "req" : "opt"}
            </button>
            <button onClick={() => moveField(idx, -1)} style={{ ...smallBtn, color: colors.textSecondary }} title="Move up">
              <ChevronUp size={14} />
            </button>
            <button onClick={() => moveField(idx, 1)} style={{ ...smallBtn, color: colors.textSecondary }} title="Move down">
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => removeField(idx)}
              style={{ ...smallBtn, color: colors.danger }}
              title="Remove field"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Add field */}
      {addingField ? (
        <div
          style={{
            padding: 16,
            background: colors.bgCard,
            border: `1px solid ${colors.accent}`,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Field Name (key)</label>
              <input
                style={inputStyle}
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                placeholder="e.g. jobTitle"
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Label</label>
              <input
                style={inputStyle}
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                placeholder="e.g. Job Title"
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Type</label>
              <select
                style={inputStyle}
                value={newField.type}
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Placeholder</label>
              <input
                style={inputStyle}
                value={newField.placeholder}
                onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>
          {newField.type === "select" && (
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Options (comma-separated)</label>
              <input
                style={inputStyle}
                value={newField.options}
                onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                placeholder="Option 1, Option 2, Option 3"
              />
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={newField.required}
                onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                style={{ accentColor: colors.accent }}
              />
              Required
            </label>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setAddingField(false)}
              style={{
                padding: "6px 14px",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                background: "transparent",
                color: colors.textSecondary,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={addField}
              disabled={!newField.name || !newField.label}
              style={{
                padding: "6px 14px",
                border: "none",
                borderRadius: 6,
                background: colors.accent,
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                cursor: !newField.name || !newField.label ? "not-allowed" : "pointer",
                opacity: !newField.name || !newField.label ? 0.5 : 1,
              }}
            >
              Add Field
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAddingField(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            border: `1px dashed ${colors.border}`,
            borderRadius: 8,
            background: "transparent",
            color: colors.accent,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            width: "100%",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <Plus size={14} /> Add Field
        </button>
      )}

      {/* Form settings */}
      <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 16, marginTop: 8 }}>
        <h4
          style={{
            fontFamily: fonts.heading,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            margin: "0 0 12px",
          }}
        >
          Form Settings
        </h4>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Submit Button Label</label>
            <input
              style={inputStyle}
              value={formConfig.submitLabel || ""}
              onChange={(e) => updateSetting("submitLabel", e.target.value)}
              placeholder="Submit"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Source Label</label>
            <select
              style={inputStyle}
              value={formConfig.sourceLabel || "landing-page"}
              onChange={(e) => updateSetting("sourceLabel", e.target.value)}
            >
              <option value="landing-page">Landing Page</option>
              <option value="newsletter-signup">Newsletter Sign-Up</option>
              <option value="webinar-registration">Webinar Registration</option>
              <option value="contact-form">Contact Form</option>
              <option value="signup">General Sign-Up</option>
              <option value="referral">Referral</option>
              <option value="event">Event</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Source Tags (comma-separated)</label>
            <input
              style={inputStyle}
              value={(formConfig.sourceTags || []).join(", ")}
              onChange={(e) => updateSetting("sourceTags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
              placeholder="lead"
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Notification Emails (comma-separated)</label>
            <input
              style={inputStyle}
              value={(formConfig.notifyEmails || []).join(", ")}
              onChange={(e) => updateSetting("notifyEmails", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
              placeholder="sales@company.com"
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Thank-You Message</label>
            <textarea
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
              value={formConfig.thankYouMessage || ""}
              onChange={(e) => updateSetting("thankYouMessage", e.target.value)}
              placeholder="Thank you for your submission."
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Thank-You Redirect URL (optional — overrides message)</label>
            <input
              style={inputStyle}
              value={formConfig.thankYouRedirect || ""}
              onChange={(e) => updateSetting("thankYouRedirect", e.target.value)}
              placeholder="https://example.com/thank-you"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
